import fallbackImg from "./assets/agd-logo-2024.jpg";

// ─── iMIS IQA endpoint + query paths ─────────────────────────────────────────
// Centralized so AGD can rename/move the IQAs without hunting through api/.

/** The iMIS REST endpoint that runs IQA queries (queryname=...). */
export const QUERY_ENDPOINT = "query";

/** $/ASI/Store/AGDStoreProductList — the CE bundle products. Supports &category= / &product= filters. */
export const IQA_PRODUCTS = "$/ASI/Store/AGDStoreProductList";

/** $/ASI/Store/CouponsAvailable — the member's available coupons. */
export const IQA_COUPONS = "$/ASI/Store/CouponsAvailable";

/** $/ASI/CouponsUsed — the member's coupon-purchase history. */
export const IQA_HISTORY = "$/ASI/CouponsUsed";

/**
 * $/ASI/Store/Purchased — products the party has purchased (coupon-redeemed
 * or full-price) that are still available in the store. Unlike
 * CouponsAvailable/CouponsUsed, this IQA does not self-scope to the request
 * party — the party ID must be passed explicitly via an `Id` param.
 */
export const IQA_PURCHASED = "$/ASI/Store/Purchased";

/** $/ASI/Store/LoggedInUser — the logged-in user's ID + IsMember flag (drives which pricing shows). */
export const IQA_LOGGED_IN_USER = "$/ASI/Store/LoggedInUser";

/**
 * $/ASI/Store/SyncBundlePurchase — the party's own bundle-purchase order
 * lines dated today, already shaped close to an AGD_Coupons insert row
 * (CouponMessage/CouponType/Inactive/ProductType/ExpiryDate computed
 * server-side; MaxUses is NOT returned — see bundleSync.ts's client-side map).
 */
export const IQA_SYNC_BUNDLE_PURCHASE = "$/ASI/Store/SyncBundlePurchase";

/** AGD logo used when a product has no image, or its image fails to load. */
export const FALLBACK_IMG = fallbackImg;

/** iMIS cart page the "View Cart" button and success toast link to. */
export const CART_URL = "/cart";

// ─── Coupon / category model ─────────────────────────────────────────────────

/** Coupons whose ProductCode is this are usable for the DE (Study Guides) category only. */
export const MBRSHP_CODE = "MBRSHP";

/**
 * Bundle categories that allow repeat purchases. Products in these categories
 * always show "Add to Cart" — never a coupon CTA or an "already purchased" state.
 */
export const BUNDLE_CATEGORIES: string[] = ["GDEXB", "IMPEXB"];

/** True when a product's category allows multiple purchases (a bundle). */
export function isBundleCategory(category: string): boolean {
  return BUNDLE_CATEGORIES.includes(category);
}

/** A "view what you own" shortcut for an already-purchased, non-bundle product. */
export interface AccessShortcut {
  label: string;
  href: string;
}

/**
 * The access shortcut for an already-purchased product: study guides (DE) link
 * to /AccessStudyGuide; everything else (GD, IMP, WEB, ...) links to /AccessModule.
 */
export function accessShortcut(category: string): AccessShortcut {
  if (category === "DE") return { label: "Access Study Guide", href: "/AccessStudyGuide" };
  return { label: "Access Module", href: "/AccessModule" };
}

/** The product categories the store knows about, in display order. */
export const CATEGORIES = ["GD", "WEB", "DE", "IMP"] as const;

/** The iMIS promotion code (zeroes the price) for each product category. */
const PROMO_BY_CATEGORY: Record<string, string> = {
  GD: "GDBundle",
  WEB: "WEBBundle",
  DE: "DEBundle",
  IMP: "IMPBundle",
};

/** The iMIS promotion for a coupon spanning multiple categories (e.g. GD+IMP). */
export const COMBO_PROMO_CODE = "ComboBundle";

/** Friendly display name for each category code. */
const LABEL_BY_CATEGORY: Record<string, string> = {
  GD: "General Dentistry",
  WEB: "Webinars",
  DE: "Study Guides",
  IMP: "Impact",
};

/** The iMIS promotion for a product category, or null if none is configured. */
export function promoForCategory(category: string): string | null {
  return PROMO_BY_CATEGORY[category] ?? null;
}

/** Friendly name for a product/coupon category code (falls back to the code itself). */
export function categoryLabel(category: string): string {
  return LABEL_BY_CATEGORY[category] ?? category;
}

/** Login URL for the "sign in to see your coupons" nudge, returning to the current page. */
export function signInUrl(): string {
  const returnTo = encodeURIComponent(location.pathname + location.search);
  return `${location.origin}/_AGD/Sign_In.aspx?LoginRedirect=true&returnurl=${returnTo}`;
}
